import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

import { apiService } from "../../services/api.service";
import { helperService } from "../../services/helper.service";
import { toast } from "../../components/Toast/ToastManager";
import LoadingSpinner from "../../components/LoadingSpinner";

const StripeConnectReturn: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const code = searchParams.get("code");
    const state = searchParams.get("state"); // club_id
    const error = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    if (error) {
      setErrorMessage(errorDescription || error);
      setStatus("error");
      toast.show({
        title: "Stripe Connect Failed",
        content: errorDescription || "Failed to connect your Stripe account.",
        duration: 8000,
        type: "fail",
      });
      setTimeout(() => navigate("/balances-payouts"), 4000);
      return;
    }

    if (!code || !state) {
      setErrorMessage("Missing authorization code or state parameter.");
      setStatus("error");
      setTimeout(() => navigate("/balances-payouts"), 4000);
      return;
    }

    apiService
      .exchangeStripeConnectCode(state, code)
      .then(() => {
        setStatus("success");
        toast.show({
          title: "Stripe Account Connected",
          content: "Your Stripe account has been successfully connected.",
          duration: 5000,
          type: "success",
        });
        setTimeout(() => navigate("/balances-payouts"), 2000);
      })
      .catch((err: any) => {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to connect your Stripe account. Please try again.";
        setErrorMessage(msg);
        setStatus("error");
        toast.show({
          title: "Stripe Connect Failed",
          content: msg,
          duration: 8000,
          type: "fail",
        });
        setTimeout(() => navigate("/balances-payouts"), 4000);
      });
  }, [searchParams, navigate]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        gap: "16px",
        padding: "40px",
      }}
    >
      {status === "loading" && (
        <>
          <LoadingSpinner />
          <p style={{ color: "#555" }}>Connecting your Stripe account…</p>
        </>
      )}
      {status === "success" && (
        <>
          <span style={{ fontSize: "48px" }}>✓</span>
          <h2>Stripe Account Connected</h2>
          <p style={{ color: "#555" }}>Redirecting to Balances & Payouts…</p>
        </>
      )}
      {status === "error" && (
        <>
          <span style={{ fontSize: "48px" }}>✗</span>
          <h2>Connection Failed</h2>
          <p style={{ color: "#d0392d" }}>{errorMessage}</p>
          <p style={{ color: "#555" }}>Redirecting back…</p>
        </>
      )}
    </div>
  );
};

export default StripeConnectReturn;
